import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Zap, Search, MessageCircle, SplitSquareVertical, ShieldCheck, CheckCircle } from 'lucide-react';
import { FaqSection } from '../components/sections/FaqSection';

interface RoommateLandingPageProps {
  navigateTo: (page: string, data?: any) => void;
  onJoinWaitingList: () => void;
}

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

export const RoommateLandingPage = ({ navigateTo, onJoinWaitingList }: RoommateLandingPageProps) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-black selection:bg-amber-500/30 font-sans overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[820px] lg:min-h-screen flex items-center overflow-hidden">
        {/* Hero Background - Cinematic Interior */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://firebasestorage.googleapis.com/v0/b/qualified-root-298415.firebasestorage.app/o/media%2FChatGPT%20Image%20Apr%2021%2C%202026%2C%2003_20_06%20PM-Picsart-AiImageEnhancer.png?alt=media&token=0f4200dd-181c-49af-89f3-0edc89a3b488" 
            alt="Interior" 
            className="absolute inset-0 w-full h-full object-cover object-bottom"
            referrerPolicy="no-referrer"
          />
          {/* Gradients to ensure text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 lg:from-black/10 lg:via-black/5 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40"></div>
        </div>

        <div className="container relative z-10 w-full mx-auto px-6 py-20 lg:pt-40 lg:pb-32 flex flex-col lg:flex-row items-center justify-between gap-24 lg:gap-12">
          {/* Text Content */}
          <div className="flex-1 text-center lg:text-left">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.h1 variants={fadeUp} className="text-4xl lg:text-[72px] text-white font-extrabold tracking-tighter leading-[1.05] mb-6 px-6 md:px-0">
              Shared living, <br className="hidden lg:block" />
              upgraded
            </motion.h1>
            <motion.p variants={fadeUp} className="text-md lg:text-xl text-gray-200 mb-6 max-w-xl mx-auto lg:mx-0 leading-relaxed font-light">
              Find your perfect match, apply together, and split bills seamlessly. Coleese makes having roommates stress-free.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <button onClick={onJoinWaitingList} className="w-2/3 sm:w-auto px-8 py-4 rounded-full bg-black text-white text-sm font-bold lg:text-md hover:bg-gray-800 transition-transform active:scale-95 flex items-center justify-center gap-2">
                Get access
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* 3D Visual */}
        <motion.div 
          className="flex-1 z-10 flex justify-center lg:justify-end perspective-1000"
          initial={{ opacity: 0, scale: 0.9, rotateY: 20 }}
          animate={{ opacity: 1, scale: 1, rotateY: -5 }}
          transition={{ duration: 1.2, delay: 0.2, type: "spring", bounce: 0.4 }}
        >
          {/* Placeholder for a nice 3D image or illustration representing roommates/splitting bills */}
          <div className="relative w-full max-w-md aspect-square rounded-[40px] overflow-hidden flex lg:pt-20 items-start justify-start transform transition-transform hover:rotate-y-12 hover:rotate-x-12 duration-500">
             <div className="absolute inset-0"></div>
             <div className="relative z-10 flex flex-col items-center gap-6">
                <div className="flex -space-x-4">
                    <img className="w-20 h-20 rounded-full border-2 border-white shadow-lg object-cover" src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&q=80" alt="User 1" />
                    <img className="w-20 h-20 rounded-full border-2 border-white shadow-lg object-cover" src="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=200&q=80" alt="User 2" />
                </div>
                <div className="bg-white text-sm px-6 py-3 rounded-full shadow-md font-bold lg:text-lg flex items-center gap-2">
                    <SplitSquareVertical size={20} className="text-blue-500" /> Rent Split 50/50
                </div>
             </div>
          </div>
        </motion.div>
      </div>
    </section>

      {/* NEW: Payment Pot Highlight Section */}
      <section className="py-20 px-6 max-w-7xl mx-auto overflow-hidden">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Visual Side (Left) */}
          <motion.div 
            className="flex-1 relative"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="relative rounded-[40px] overflow-hidden shadow-2xl">
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/qualified-root-298415.firebasestorage.app/o/media%2Fpexels-diva-plavalaguna-6150520.jpg?alt=media&token=e42d0115-e8ab-4670-83e0-d17cde1182db" 
                alt="Roommates having fun" 
                className="w-full aspect-[4/5] object-cover"
              />
              
              {/* Overlay Cards - Individual Tracking vs. Collective Progress */}
              <div className="absolute bottom-8 left-8 right-8 space-y-3 z-10 font-sans">
                {/* Personal Share Card */}
                <div className="bg-black/90 backdrop-blur-lg p-5 rounded-2xl flex items-center justify-between text-white border border-white/10 shadow-2xl">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                       <Zap size={20} className="text-black" />
                    </div>
                    <div>
                      <p className="font-bold text-sm">Your share</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">R 4,500 remaining</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-0.5">Oct Rent</p>
                    <p className="font-black text-base">R 12,000</p>
                  </div>
                </div>
                
                {/* Group Progress Card */}
                <div className="bg-[#1a1a1a]/95 backdrop-blur-lg p-5 rounded-2xl text-white border border-white/5 shadow-2xl">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center border border-blue-500/30">
                         <Users size={16} />
                      </div>
                      <p className="font-bold text-sm">Group Progress</p>
                    </div>
                    <p className="text-sm font-black text-blue-400">75%</p>
                  </div>
                  
                  {/* Progress Bar Component */}
                  <div className="w-full bg-white/10 rounded-full h-2 mb-2 overflow-hidden">
                    <div className="bg-blue-500 h-full rounded-full w-3/4 relative">
                        <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center text-[10px] font-bold text-gray-400 uppercase tracking-tight">
                    <span>R 15,000 collected</span>
                    <span>R 20,000 Goal</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Text Side (Right) */}
          <motion.div 
            className="flex-1"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          >
            <h2 className="text-4xl lg:text-6xl font-extrabold tracking-tight mb-8">Split bills with roommates</h2>
            <div className="space-y-6">
              <p className="text-lg lg:text-xl text-gray-600 font-light leading-relaxed">
                Going halves on rent or splitting utilities with a group couldn't be simpler. Share the load easily with your co-lessees through Payment Pots—flexible sub-accounts that track everyone's contribution in real-time.
              </p>
              <p className="text-sm text-gray-400 font-medium tracking-wide">
                Set custom splits, track collective progress, and ensure everyone pays their fair share without the drama.
              </p>
              <div className="pt-4">
                <button 
                  onClick={() => navigateTo('listings', { initialFilters: { listingType: 'roommate' } })}
                  className="px-8 py-4 rounded-full bg-black text-white font-bold hover:bg-gray-800 transition-all active:scale-95 shadow-lg"
                >
                  Start splitting
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Features Section (White Background) */}
      <section className="-mt-10 lg:-mt-20 py-20 px-6 bg-white text-black rounded-t-[40px] lg:rounded-t-[80px] relative z-20">
        <motion.div 
          className="max-w-7xl mx-auto"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeUp} className="text-center mb-10">
            <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight mb-6">Everything you need to co-live</h2>
            <p className="text-gray-600 text-base lg:text-lg max-w-3xl mx-auto font-light leading-relaxed">
              From finding the right person to managing the lease together, we've got you covered.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Matchmaking */}
            <motion.div variants={fadeUp} className="bg-[#f8f8f8] rounded-[32px] p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center mb-8 shadow-lg">
                 <Search size={20} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Smart Matchmaking</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Find roommates based on lifestyle, budget, and habits. Say goodbye to awkward living situations.
              </p>
            </motion.div>

            {/* Joint Applications */}
            <motion.div variants={fadeUp} className="bg-[#f8f8f8] rounded-[32px] p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 rounded-full bg-[#2088ff] flex items-center justify-center mb-8 shadow-lg">
                <Users size={20} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Joint Applications</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Combine your Passports to apply as a group. Landlords see your combined strength in one click.
              </p>
            </motion.div>

            {/* Bill Splitting */}
            <motion.div variants={fadeUp} className="bg-[#f8f8f8] rounded-[32px] p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-[#fd297b] to-[#ff655b] flex items-center justify-center mb-8 shadow-lg">
                <SplitSquareVertical size={20} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Automated Bill Splitting</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Rent and utilities are automatically divided. Everyone pays their share directly—no more chasing friends for money.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Trust & Safety Section (Dark Mode) */}
      <section className="py-32 px-6 bg-[#111] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-500/10 rounded-full blur-[150px] pointer-events-none"></div>
        
        <motion.div 
          className="max-w-7xl mx-auto relative z-10"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="flex-1">
                <motion.h2 variants={fadeUp} className="text-3xl lg:text-4xl font-extrabold mb-8 tracking-tight">
                    Built on trust <br className="hidden md:block" />and transparency
                </motion.h2>
                <motion.p variants={fadeUp} className="text-gray-400 text-base mb-8 font-light max-w-xl">
                    We verify every user so you can feel secure about who you're moving in with.
                </motion.p>
                
                <motion.ul variants={fadeUp} className="space-y-6">
                    <li className="flex items-start gap-4">
                        <ShieldCheck className="text-blue-400 mt-1 flex-shrink-0" size={24} />
                        <div>
                            <h4 className="font-bold text-lg">Verified Identities</h4>
                            <p className="text-gray-400 text-sm mt-1">Every roommate profile requires ID verification.</p>
                        </div>
                    </li>
                    <li className="flex items-start gap-4">
                        <MessageCircle className="text-blue-400 mt-1 flex-shrink-0" size={24} />
                        <div>
                            <h4 className="font-bold text-lg">Secure Messaging</h4>
                            <p className="text-gray-400 text-sm mt-1">Chat safely within the app before sharing personal details.</p>
                        </div>
                    </li>
                </motion.ul>
            </div>
            
            <div className="flex-1 w-full">
                <motion.div variants={fadeUp} className="bg-[#1a1a1a] border border-white/10 rounded-[32px] p-8 shadow-2xl">
                    <h3 className="text-xl font-bold mb-6">How it works</h3>
                    <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-white/20 before:to-transparent">
                        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-[#111] text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2">
                                1
                            </div>
                            <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-white/10 bg-white/5">
                                <h4 className="font-bold text-sm">Create your profile</h4>
                            </div>
                        </div>
                        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white/20 bg-[#111] text-gray-400 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2">
                                2
                            </div>
                            <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-white/5 bg-transparent">
                                <h4 className="font-bold text-sm text-gray-400">Match & Chat</h4>
                            </div>
                        </div>
                        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white/20 bg-[#111] text-gray-400 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2">
                                3
                            </div>
                            <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-white/5 bg-transparent">
                                <h4 className="font-bold text-sm text-gray-400">Apply</h4>
                            </div>
                        </div>
                        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white/20 bg-[#111] text-gray-400 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2">
                                4
                            </div>
                            <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-white/5 bg-transparent">
                                <h4 className="font-bold text-sm text-gray-400">Get approved</h4>
                            </div>
                        </div>
                        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white/20 bg-[#111] text-gray-400 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2">
                                5
                            </div>
                            <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-white/5 bg-transparent">
                                <h4 className="font-bold text-sm text-gray-400">Move-in</h4>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-6 text-center bg-white text-black border-t border-gray-100">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.h2 variants={fadeUp} className="text-3xl lg:text-4xl font-extrabold mb-8 tracking-tight max-w-4xl mx-auto">Ready to find your perfect roommate?</motion.h2>
          <motion.p variants={fadeUp} className="text-gray-600 text-base mb-8 font-light">Join the platform that makes shared living easy.</motion.p>
          <motion.button variants={fadeUp} onClick={onJoinWaitingList} className="px-10 py-5 rounded-full bg-black text-white font-bold text-sm lg:text-md hover:bg-gray-800 transition-transform active:scale-95">
            Get access
          </motion.button>
        </motion.div>
      </section>

      <FaqSection />
    </div>
  );
};
